import React, { useState } from 'react';
import styled, { css, ThemeConsumer } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { mq } from '../../../../../../common/src/utils/styledComponentsUtils';

// Components
import Slider, { Handle } from 'rc-slider';
import 'rc-slider/assets/index.css';

// Types
import { LanguageStringMap } from '../../../../../../../types/Common';
import { handleResponseLanguage } from '../../../../../../../i18n';
import { Box } from '@wegroup/design-system';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import useQuestionEnum from '../../../../hooks/useQuestionEnum';

interface Props {
  question: FlowsQuestion;
}

interface RangeObject {
  min: number;
  max: number;
  interval: number;
  step?: number;
  default: number;
  unit?: LanguageStringMap | null;
  type: 'BETWEEN' | 'EXACT';
  inclusive: boolean;
}

const data: RangeObject = {
  min: 5000,
  max: 30000,
  interval: 2500,
  step: 500,
  default: 12501,
  unit: {
    en: 'kilometers / year',
    fr: 'kilomètres / an',
    nl: 'kilometers / jaar',
  },
  type: 'BETWEEN',
  inclusive: false,
};

const items = (data.max - data.min) / data.interval + 1;
const marks = Object.assign(
  {},
  ...[...Array(items)].map((_, index) => ({
    [(index + 2) * data.interval]: {
      label:
        (data.type === 'BETWEEN'
          ? index === 0
            ? '< '
            : index === items - 1
            ? '> '
            : ''
          : '') +
        (index + 2) * data.interval,
    },
  })),
);

const KilometerSliderInputComponent: React.FC<
  React.PropsWithChildren<Props>
> = ({ question }) => {
  const { t } = useTranslation();

  const {
    field: { value, onBlur },
  } = useFlowInputComponent({
    question,
    defaultValue: 'BETWEEN_12500_AND_15000',
  });

  const options = useQuestionEnum(question).map(({ value }) => value);

  const getEnum = (value: number) => {
    if (value <= 5000) return options[0];
    if (value < 7500) return options[1];
    if (value < 10000) return options[2];
    if (value < 12500) return options[3];
    if (value < 15000) return options[4];
    if (value < 20000) return options[5];
    if (value < 25000) return options[6];
    if (value < 30000) return options[7];
    return options[8];
  };

  const getValue = (value: string) => {
    if (value === options[0]) return 5000;
    if (value === options[1]) return 5001;
    if (value === options[2]) return 7501;
    if (value === options[3]) return 10001;
    if (value === options[4]) return 12501;
    if (value === options[5]) return 15001;
    if (value === options[6]) return 20001;
    if (value === options[7]) return 25000;
    if (value === options[8]) return 30000;
  };

  const [sliderValue, setSliderValue] = useState<number>(
    getValue(value as string) || data.default,
  );

  const handleOnChange = (value: number) => {
    setSliderValue(value);
  };

  const toDottedNumber = (plainNumber: number) =>
    plainNumber.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  const getBetween = (value: number) => {
    if (value === data.min)
      return t('Less then x unit', {
        x: toDottedNumber(data.min),
        unit: handleResponseLanguage(data.unit),
      });
    if (value === data.max)
      return t('More then x unit', {
        x: toDottedNumber(data.max),
        unit: handleResponseLanguage(data.unit),
      });

    const counter = Math.floor(sliderValue / data.interval);

    return t('Between x and y unit', {
      x: toDottedNumber(counter * data.interval),
      y: toDottedNumber((counter + 1) * data.interval),
      unit: handleResponseLanguage(data.unit),
    });
  };

  const numValue = getValue(value as string) || 0;

  return (
    <Wrapper
      data-test-id="ProdFac_SliderInputComponent"
      inclusive={data.inclusive}
    >
      <Current data-test-id="ProdFac_SliderInputComponent_Between">
        {data.type === 'BETWEEN' ? getBetween(sliderValue) : sliderValue}
      </Current>
      <ThemeConsumer>
        {(theme) => (
          <Slider
            min={data.min}
            max={data.max}
            railStyle={{ backgroundColor: theme.gray[100] }}
            trackStyle={{ backgroundColor: theme.primary[500] }}
            dotStyle={{
              backgroundColor: theme.gray[100],
              borderColor: theme.gray[100],
            }}
            activeDotStyle={{
              backgroundColor: theme.primary[500],
              borderColor: theme.primary[500],
            }}
            handleStyle={{
              borderColor: theme.primary[500],
              borderWidth: '4px',
              width: '2.4rem',
              height: '2.4rem',
              marginTop: '-1rem',
            }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            handle={(handleProps: any) => (
              <Handle {...handleProps}>
                <Box
                  width="16"
                  height="16"
                  position="absolute"
                  top="-6"
                  left="-6"
                />
              </Handle>
            )}
            defaultValue={numValue || data.default}
            onChange={handleOnChange}
            onBlur={() =>
              onBlur({ name: question.id, value: getEnum(sliderValue) })
            }
            marks={marks}
          />
        )}
      </ThemeConsumer>
    </Wrapper>
  );
};

const Current = styled.div`
  font-weight: 500;
  width: 100%;
  text-align: center;
  margin-bottom: 1.6rem;
  min-height: 5.6rem;
`;

const Wrapper = styled.div<{ inclusive?: boolean }>`
  width: 100%;
  padding: 4rem 2.4rem;

  .rc-slider-handle {
    position: relative;
  }

  .rc-slider-handle:active {
    box-shadow: 0 0 5px ${({ theme }) => theme.primary[300]};
  }

  ${({ inclusive }) =>
    inclusive &&
    css`
      .rc-slider-dot {
        &:first-of-type,
        &:last-of-type {
          display: none;
        }
      }
    `}

  .rc-slider-mark-text,
  .rc-slider-mark-text-active {
    color: #8990a3;
    white-space: nowrap;
    margin-top: 0.8rem;

    ${mq.laptop} {
      font-size: 1.6rem;
      margin-top: 0;
    }
  }

  .rc-slider-mark-text {
    display: none;

    &:first-of-type {
      display: block;
    }

    &:last-of-type {
      display: block;
    }

    ${mq.tablet} {
      display: block;
    }
  }
`;

export default KilometerSliderInputComponent;
